import React, { useState } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab } from '@mui/material';
import { ContentCopy, FiberManualRecord, CheckCircle } from '@mui/icons-material';
import { CodeExample, CodeVariant } from '../../types/documentation';
import LinkedText from '../common/LinkedText';

interface CodeExamplesSectionProps {
  codeExamples: CodeExample[];
}

const CodeExamplesSection: React.FC<CodeExamplesSectionProps> = ({
  codeExamples
}) => {
  const [selectedTabs, setSelectedTabs] = useState<{ [key: number]: number }>({});

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const handleTabChange = (exampleIndex: number, newValue: number) => {
    setSelectedTabs(prev => ({
      ...prev,
      [exampleIndex]: newValue
    }));
  };

  if (!codeExamples || codeExamples.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 4, textAlign: 'left' }}>
      {codeExamples.map((codeExample, index) => (
        <Box key={index} sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, textAlign: 'left' }}>
            {codeExample.title}
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary" sx={{ textAlign: 'left' }}>
            <LinkedText text={codeExample.description} />
          </Typography>
          {codeExample.bulletPoints && codeExample.bulletPoints.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {codeExample.bulletPoints.map((point, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1,
                    textAlign: 'left'
                  }}
                >
                  <FiberManualRecord
                    sx={{
                      fontSize: '0.5rem',
                      mt: 0.7,
                      mr: 1.5,
                      color: 'primary.main',
                      flexShrink: 0
                    }}
                  />
                  <Typography variant="body2" color="text.primary">
                    <LinkedText text={point} />
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {codeExample.table && codeExample.table.headers && codeExample.table.rows && (
            <TableContainer component={Paper} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    {codeExample.table.headers.map((header, idx) => (
                      <TableCell
                        key={idx}
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          borderBottom: '2px solid #e0e0e0'
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {codeExample.table.rows.map((row, rowIdx) => (
                    <TableRow
                      key={rowIdx}
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                        '&:hover': { bgcolor: '#f0f0f0' }
                      }}
                    >
                      {row.map((cell, cellIdx) => (
                        <TableCell key={cellIdx}>
                          {cell === '✓' || cell === '✔' || cell.toLowerCase() === 'yes' || cell.toLowerCase() === 'true' ? (
                            <CheckCircle sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                          ) : (
                            cell
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {codeExample.image && (
            <Box
              component="img"
              src={codeExample.image}
              alt={codeExample.imageAlt || 'Step illustration'}
              sx={{
                mt: 2,
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
          )}
          {codeExample.code && (
            <Box>
              {typeof codeExample.code === 'string' ? (
                // Simple string code format
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    bgcolor: '#f8f9fa',
                    position: 'relative',
                    border: '1px solid #e9ecef'
                  }}
                >
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      overflow: 'auto',
                      color: '#212529',
                      textAlign: 'left'
                    }}
                  >
                    {codeExample.code}
                  </Box>
                  <Tooltip title="Copy code">
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: 'white',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                      onClick={() => handleCopyCode(codeExample.code as string)}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>
              ) : (
                // Multi-language code format with tabs
                <Box>
                  <Tabs
                    value={selectedTabs[index] || 0}
                    onChange={(_, newValue) => handleTabChange(index, newValue)}
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: '#f8f9fa',
                      borderTopLeftRadius: 4,
                      borderTopRightRadius: 4,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        minHeight: 42,
                        color: '#666',
                        '&.Mui-selected': {
                          color: '#1976d2',
                          fontWeight: 600
                        }
                      }
                    }}
                  >
                    {(codeExample.code as CodeVariant[]).map((variant, idx) => (
                      <Tab
                        key={idx}
                        label={variant.label || variant.language}
                        id={`code-tab-${index}-${idx}`}
                        aria-controls={`code-tabpanel-${index}-${idx}`}
                      />
                    ))}
                  </Tabs>
                  {(codeExample.code as CodeVariant[]).map((variant, idx) => (
                    <Box
                      key={idx}
                      role="tabpanel"
                      hidden={selectedTabs[index] !== idx && (selectedTabs[index] !== undefined || idx !== 0)}
                      id={`code-tabpanel-${index}-${idx}`}
                      aria-labelledby={`code-tab-${index}-${idx}`}
                    >
                      {(selectedTabs[index] === idx || (selectedTabs[index] === undefined && idx === 0)) && (
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            bgcolor: '#f8f9fa',
                            position: 'relative',
                            border: '1px solid #e9ecef',
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            borderTop: 'none'
                          }}
                        >
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                              overflow: 'auto',
                              color: '#212529',
                              textAlign: 'left'
                            }}
                          >
                            {variant.code}
                          </Box>
                          <Tooltip title="Copy code">
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                backgroundColor: 'white',
                                '&:hover': {
                                  backgroundColor: '#f5f5f5'
                                }
                              }}
                              onClick={() => handleCopyCode(variant.code)}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Paper>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default CodeExamplesSection;
